// Category rollup report form (RMN portal). Picks a node from the network's
// crawled category tree (GET /rmn/categories), then picks WHICH of the
// products under that node to audit (GET /rmn/categories/:id/report-targets
// — the same resolution the job runs: IMPORTED products with an active PDP
// URL only; still-discovered rows must be imported first), and enqueues an
// async report via POST /rmn/audit/category-report { rmn_category_id,
// target_keys }. Everything is ticked by default, so the plain "report on
// this category" path is still one click.
// The report generates in the background; the parent page lists it with a
// Generating badge and polls.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TravelExploreOutlined } from "@mui/icons-material";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { useNetworkScope } from "../../hooks/useNetworkScope";
import {
    rmnAuditCategoryReportEndpoint,
    rmnCategoriesEndpoint,
    rmnCategoryReportTargetsEndpoint
} from "../../api/endpoints";
import { apiErrorCode } from "../../utils/apiError";
import { RmnCategoryNode, RmnCategoryReportTarget } from "../../types/rmn";
import { ScoreChip } from "../../pages/network/catalogShared";
import { BLACK, BORDER, CARD_BG, FONT, GOLD, GOLD_DARK, TEXT_FAINT, TEXT_MUTED } from "../ProposalFlow/theme";

const ERROR = "var(--tool-down, #c62828)";

interface FlatCategoryOption {
    id: number;
    label: string;
    depth: number;
}

// Amber note when products under the target are still in Discovered —
// they are not analyzed until imported, so the count the client expects
// and the count that runs can differ.
export function DiscoveredWarning({ count, noun, href }: { count: number; noun: string; href?: string }) {
    if (!count) return null;
    return (
        <div
            role="note"
            style={{
                fontFamily: FONT,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "var(--tool-warn-text, #7A5A00)",
                background: "var(--tool-warn-bg, #FFF6D6)",
                border: "1px solid var(--tool-warn-border, #F1DD8C)",
                borderRadius: 10,
                padding: "8px 12px",
                marginBottom: 12,
            }}
        >
            {count.toLocaleString("en-US")} {count === 1 ? "product" : "products"} {noun} still in
            Discovered and won&apos;t be analyzed until imported.
            {href && (
                <>
                    {" "}
                    <Link to={href} style={{ color: "inherit", fontWeight: 600, textDecoration: "underline" }}>
                        View discovered products
                    </Link>
                </>
            )}
        </div>
    );
}

// Amber note + action for a target with NOTHING under it (no imported and
// no discovered products): the fix is to analyze it, the same action as the
// row's Analyze button. `disabledReason` mirrors the row's disabled state.
export function NothingFoundWarning({
    what,
    onAnalyze,
    disabledReason,
}: {
    what: string;
    onAnalyze?: () => void;
    disabledReason?: string | null;
}) {
    return (
        <div
            role="note"
            style={{
                fontFamily: FONT,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "var(--tool-warn-text, #7A5A00)",
                background: "var(--tool-warn-bg, #FFF6D6)",
                border: "1px solid var(--tool-warn-border, #F1DD8C)",
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <span>
                Nothing has been found for {what} yet — analyze it to discover its products, then import
                the ones you want scored.
            </span>
            {onAnalyze && (
                <button
                    type="button"
                    onClick={onAnalyze}
                    disabled={!!disabledReason}
                    title={disabledReason || `Analyze ${what}`}
                    className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-surface cursor-pointer hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0 inline-flex items-center gap-1"
                >
                    <TravelExploreOutlined sx={{ fontSize: 15 }} /> Analyze
                </button>
            )}
        </div>
    );
}

// Find one node anywhere in the nested tree.
function findNode(nodes: RmnCategoryNode[], id: number): RmnCategoryNode | null {
    return nodes.reduce<RmnCategoryNode | null>((found, n) => {
        if (found) return found;
        if (n.id === id) return n;
        return n.children && n.children.length ? findNode(n.children, id) : null;
    }, null);
}

// Depth-first flatten of the nested tree into indented <select> options.
function flattenTree(nodes: RmnCategoryNode[], out: FlatCategoryOption[] = []): FlatCategoryOption[] {
    nodes.forEach((node) => {
        // Label with the same "Catalog" number the categories table shows
        // (imported products in the subtree) — never the crawler's page
        // count, which measures something else.
        const count =
            typeof node.catalog_product_count === "number"
                ? ` (${node.catalog_product_count} in catalog)`
                : "";
        out.push({ id: node.id, label: `${node.name}${count}`, depth: node.depth || 0 });
        if (node.children && node.children.length > 0) flattenTree(node.children, out);
    });
    return out;
}

const fieldLabelStyle: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: 600,
    color: BLACK,
    display: "block",
    marginBottom: 8
};

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "14px 16px",
    fontFamily: FONT,
    fontSize: 14,
    border: `1.5px solid ${BORDER}`,
    borderRadius: 12,
    outline: "none",
    boxSizing: "border-box",
    color: BLACK,
    background: CARD_BG
};

interface Props {
    // Fired with the new report id after the backend accepts the request.
    onCreated: (id: number) => void;
    // Pre-selects a category (the Intelligence Categories page's per-row
    // "Run report" action passes the clicked node).
    defaultCategoryId?: number;
    // Starts the category analysis (the row's Analyze action) when the
    // picked category has nothing imported AND nothing discovered.
    onAnalyze?: (node: RmnCategoryNode) => void;
    // Why Analyze is unavailable for this node (URL not analyzable, analysis
    // already running…) — null/undefined = available.
    analyzeDisabledReason?: (node: RmnCategoryNode) => string | null;
}

export function CategoryAuditForm({ onCreated, defaultCategoryId, onAnalyze, analyzeDisabledReason }: Props) {
    const [tree, setTree] = useState<RmnCategoryNode[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [categoryId, setCategoryId] = useState<number | "">(defaultCategoryId || "");
    const { basePath } = useNetworkScope();
    const [targets, setTargets] = useState<RmnCategoryReportTarget[] | null>(null);
    const [targetsError, setTargetsError] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstant.get(rmnCategoriesEndpoint);
                if (!cancelled) setTree((res.data || []) as RmnCategoryNode[]);
            } catch {
                if (!cancelled) setLoadError("Failed to load your network's categories. Please try again.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // The chosen category's auditable products, everything ticked. Reloads
    // whenever the category changes; a failure here is not fatal — the form
    // falls back to the whole-category run.
    useEffect(() => {
        if (!categoryId) {
            setTargets(null);
            setSelected(new Set());
            return undefined;
        }
        let cancelled = false;
        setTargets(null);
        setTargetsError(false);
        (async () => {
            try {
                const res = await axiosInstant.get(
                    rmnCategoryReportTargetsEndpoint(categoryId as number)
                );
                if (cancelled) return;
                const rows = (res.data || []) as RmnCategoryReportTarget[];
                setTargets(rows);
                setSelected(new Set(rows.map((r) => r.key)));
            } catch {
                if (cancelled) return;
                setTargets([]);
                setTargetsError(true);
                setSelected(new Set());
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [categoryId]);

    const options = useMemo(() => (tree ? flattenTree(tree) : []), [tree]);
    // Discovered (not yet imported) rows under the picked subtree — the
    // categories table's "Discovered" number.
    const selectedNode = useMemo(
        () => (tree && categoryId !== "" ? findNode(tree, Number(categoryId)) : null),
        [tree, categoryId]
    );
    const discoveredCount = (selectedNode && selectedNode.discovered_product_count) || 0;

    const allSelected = useMemo(
        () => !!targets && targets.length > 0 && selected.size === targets.length,
        [targets, selected]
    );

    // Nothing to audit: the picker loaded with no imported products under the
    // category, or every row was unticked.
    const nothingImported = !!targets && targets.length === 0;
    const nothingPicked = nothingImported || (!!targets && selected.size === 0);

    const toggle = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!categoryId || submitting || nothingPicked) return;
        setSubmitting(true);
        setFormError(null);
        try {
            // No product limit — the report audits every product under the
            // category (subcategories included). Send the picks only when
            // they narrow the run: an untouched form, an empty category and
            // a picker that failed to load all stay the plain
            // "whole category" request.
            const narrowed = !!targets && targets.length > 0 && !allSelected;
            const { data } = await axiosInstant.post(rmnAuditCategoryReportEndpoint, {
                rmn_category_id: categoryId,
                ...(narrowed ? { target_keys: [...selected] } : {})
            });
            toast.success('Category PDP analysis started — it will appear in your reports list while it runs.');
            onCreated(data?.id);
        } catch (err: any) {
            const status = err?.response?.status;
            const code = apiErrorCode(err);
            let message = 'Something went wrong starting the report. Please try again.';
            if (code === 'CategoryNotFound') {
                message = 'That category is no longer available. Refresh the page and pick another.';
            } else if (code === 'NotEnoughProductsInCategory') {
                message =
                    'This category has no imported products to analyze yet — import products under it first.';
            } else if (status === 429) {
                message = 'You\'ve reached today\'s limit for advanced reports. Please try again tomorrow.';
            }
            setFormError(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loadError) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: ERROR }}>{loadError}</div>
        );
    }

    if (tree === null) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT }}>Loading categories…</div>
        );
    }

    if (options.length === 0) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_MUTED, lineHeight: 1.6 }}>
                No categories are available yet. Category reports become available once your
                network&apos;s catalog has been analyzed — contact your Desire Company account manager.
            </div>
        );
    }

    const disabled = submitting || !categoryId || nothingPicked;

    return (
        <form onSubmit={handleSubmit} noValidate>
            <div style={{ marginBottom: 20 }}>
                <label htmlFor="category-report-category" style={fieldLabelStyle}>
                    Category
                </label>
                <select
                    id="category-report-category"
                    value={categoryId}
                    disabled={submitting}
                    onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : "")}
                    style={{ ...inputStyle, appearance: "auto", cursor: "pointer" }}
                >
                    <option value="">Select a category…</option>
                    {options.map((o) => (
                        <option key={o.id} value={o.id}>
                            {"\u00A0".repeat(o.depth * 3)}
                            {o.label}
                        </option>
                    ))}
                </select>
                <div style={{ fontFamily: FONT, fontSize: 12, color: TEXT_FAINT, marginTop: 6 }}>
                    We&apos;ll analyze the imported product pages you pick below — subcategories
                    included — and roll the findings into one report. Products still in Discovered
                    aren&apos;t analyzed until they&apos;re imported. Large categories can take a while.
                </div>
            </div>

            {categoryId !== "" && (
                <div style={{ marginBottom: 20 }}>
                    <DiscoveredWarning
                        count={discoveredCount}
                        noun="under this category are"
                        href={`${basePath}/products/discovered?category=${categoryId}`}
                    />
                    {targets === null && !targetsError && (
                        <div style={{ fontFamily: FONT, fontSize: 13, color: TEXT_FAINT }}>
                            Loading this category&apos;s products…
                        </div>
                    )}
                    {targets !== null && targets.length === 0 && !targetsError && discoveredCount === 0 && (
                        <NothingFoundWarning
                            what={selectedNode ? selectedNode.name : "this category"}
                            onAnalyze={onAnalyze && selectedNode ? () => onAnalyze(selectedNode) : undefined}
                            disabledReason={
                                analyzeDisabledReason && selectedNode ? analyzeDisabledReason(selectedNode) : null
                            }
                        />
                    )}
                    {targets !== null && targets.length === 0 && (targetsError || discoveredCount > 0) && (
                        <div
                            style={{
                                fontFamily: FONT,
                                fontSize: 13,
                                color: TEXT_FAINT,
                                lineHeight: 1.6
                            }}
                        >
                            {targetsError
                                ? "We couldn't list this category's products — the report will audit everything under it."
                                : "No imported products under this category yet. Import products from Discovered first — only imported products are analyzed."}
                        </div>
                    )}
                    {targets !== null && targets.length > 0 && (
                        <>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[13px] font-semibold text-text-1">
                                    {selected.size} of {targets.length} selected
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setSelected(
                                            allSelected
                                                ? new Set()
                                                : new Set(targets.map((t) => t.key))
                                        )
                                    }
                                    className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2"
                                >
                                    {allSelected ? 'Select none' : 'Select all'}
                                </button>
                            </div>
                            <div className="flex flex-col border border-border-token rounded-xl overflow-hidden max-h-[320px] overflow-y-auto">
                                {targets.map((t) => (
                                    <label
                                        key={t.key}
                                        className="flex items-center gap-3 px-3 py-2.5 border-b border-border-token last:border-0 cursor-pointer hover:bg-surface-2"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected.has(t.key)}
                                            onChange={() => toggle(t.key)}
                                            className="shrink-0"
                                        />
                                        <span className="flex-1 min-w-0 flex flex-col">
                                            <span
                                                className="text-[13px] text-text-1 truncate"
                                                title={t.title || t.url}
                                            >
                                                {t.title || t.url}
                                            </span>
                                            <span className="text-[11px] text-muted truncate" title={t.url}>
                                                {t.url.replace(/^https?:\/\/(www\.)?/, '')}
                                            </span>
                                        </span>
                                        <ScoreChip score={t.latest_score} title="Latest score for this page" />
                                    </label>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {formError && (
                <div style={{ fontFamily: FONT, fontSize: 13, color: ERROR, marginBottom: 16 }}>
                    {formError}
                </div>
            )}

            <button
                type="submit"
                disabled={disabled}
                style={{
                    width: "100%",
                    padding: 14,
                    borderRadius: 12,
                    border: "none",
                    background: disabled ? BORDER : GOLD,
                    color: disabled ? "#B5AFA5" : "#fff",
                    fontFamily: FONT,
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                    if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = GOLD_DARK;
                }}
                onMouseLeave={(e) => {
                    if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = GOLD;
                }}
            >
                {submitting ? "Starting analysis…" : "Run Category PDP Analysis"}
            </button>
            <div style={{ fontFamily: FONT, fontSize: 12, color: TEXT_FAINT, marginTop: 10, textAlign: "center" }}>
                Reports run in the background and usually take a few minutes — a bit longer while
                the fresh analysis runs first.
            </div>
        </form>
    );
}
